import type { Key, ReactNode } from "react";

import { Button, Chip, ListBox, Popover, Tag, TagGroup } from "@heroui/react";
import { CellSelect, Segment } from "@heroui-pro/react";
import { SlidersHorizontal } from "lucide-react";

/**
 * The Filters popover for a page with several filters: one trigger with a count badge, the controls inside,
 * and "Clear all" / "Apply filters" at the bottom. The caller owns the draft state and applies it in `onApply`.
 */
export function FiltersPopover({
  activeCount,
  children,
  onApply,
  onClearAll,
  isOpen,
  onOpenChange,
}: {
  activeCount: number;
  children: ReactNode;
  onApply: () => void;
  onClearAll: () => void;
  /** Control the popover (to close it on Apply); leave out to let it manage itself. */
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <Popover isOpen={isOpen} onOpenChange={onOpenChange}>
      <Button className="relative" variant="outline">
        <SlidersHorizontal className="size-4" />
        Filters
        {activeCount > 0 ? (
          <Chip color="accent" size="sm" variant="primary">
            {activeCount}
          </Chip>
        ) : null}
      </Button>
      <Popover.Content
        className="w-[min(360px,calc(100vw-2rem))]"
        placement="bottom end"
      >
        <Popover.Dialog className="flex flex-col gap-4 px-1 py-3">
          <div className="flex items-center justify-between px-3">
            <Popover.Heading className="text-sm font-semibold">
              Filters
            </Popover.Heading>
            {activeCount > 0 ? (
              <span className="text-muted text-xs">{activeCount} active</span>
            ) : null}
          </div>
          <div className="flex flex-col gap-3">{children}</div>
          <div className="flex items-center justify-between pr-3">
            <Button size="sm" variant="ghost" onPress={onClearAll}>
              Clear all
            </Button>
            <Button size="sm" onPress={onApply}>
              Apply filters
            </Button>
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

export type FilterOption = { id: string; label: string };

/** One row of the popover: the filter's name on the left, its value on the right. */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
  allLabel = "All",
}: {
  label: string;
  value: string | null;
  options: FilterOption[];
  onChange: (value: string | null) => void;
  allLabel?: string;
}) {
  return (
    <CellSelect
      aria-label={label}
      selectedKey={value ?? "__all"}
      onSelectionChange={(key) =>
        onChange(key === "__all" || key === null ? null : String(key))
      }
    >
      <CellSelect.Trigger>
        <CellSelect.Label>{label}</CellSelect.Label>
        <CellSelect.Value />
        <CellSelect.Indicator />
      </CellSelect.Trigger>
      <CellSelect.Popover>
        <ListBox>
          {[{ id: "__all", label: allLabel }, ...options].map((option) => (
            <ListBox.Item
              key={option.id}
              id={option.id}
              textValue={option.label}
            >
              {option.label}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </CellSelect.Popover>
    </CellSelect>
  );
}

/** Like `FilterSelect`, for several values at once. The row shows the chosen names, or the count past two. */
export function FilterMultiSelect({
  label,
  value,
  options,
  onChange,
  emptyLabel = "None available",
  isDisabled,
  placeholder = "Any",
}: {
  label: string;
  value: string[];
  options: FilterOption[];
  onChange: (value: string[]) => void;
  emptyLabel?: string;
  isDisabled?: boolean;
  placeholder?: string;
}) {
  return (
    <CellSelect
      aria-label={label}
      isDisabled={isDisabled}
      placeholder={placeholder}
      selectionMode="multiple"
      value={value}
      onChange={(keys) => onChange((keys ?? []).map(String))}
    >
      <CellSelect.Trigger>
        <CellSelect.Label>{label}</CellSelect.Label>
        <CellSelect.Value>
          {({ isPlaceholder }) => {
            if (isPlaceholder || value.length === 0) return placeholder;
            const names = value.map(
              (id) => options.find((o) => o.id === id)?.label ?? id,
            );

            return names.length <= 2 ? names.join(", ") : `${names.length} selected`;
          }}
        </CellSelect.Value>
        <CellSelect.Indicator />
      </CellSelect.Trigger>
      <CellSelect.Popover>
        <ListBox selectionMode="multiple">
          {options.length === 0 ? (
            <ListBox.Item id="__empty" isDisabled textValue={emptyLabel}>
              {emptyLabel}
            </ListBox.Item>
          ) : (
            options.map((option) => (
              <ListBox.Item
                key={option.id}
                id={option.id}
                textValue={option.label}
              >
                {option.label}
                <ListBox.ItemIndicator />
              </ListBox.Item>
            ))
          )}
        </ListBox>
      </CellSelect.Popover>
    </CellSelect>
  );
}

/** A small labelled switcher, such as Scraped link: Any / Linked / Not linked. */
export function FilterSegment({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 px-3">
      <span className="text-sm font-medium">{label}</span>
      <Segment
        aria-label={label}
        className="max-w-full overflow-x-auto"
        selectedKey={value}
        onSelectionChange={(key: Key) => onChange(String(key))}
      >
        {options.map((option, index) => (
          <Segment.Item key={option.id} id={option.id}>
            {index > 0 ? <Segment.Separator /> : null}
            {option.label}
          </Segment.Item>
        ))}
      </Segment>
    </div>
  );
}

export type AppliedFilter = { id: string; label: string; value: string };

/** Applied filters, each a removable tag, shown beside the trigger and never in place of it. */
export function AppliedFilters({
  filters,
  onRemove,
  onClearAll,
}: {
  filters: AppliedFilter[];
  onRemove: (id: string) => void;
  onClearAll: () => void;
}) {
  if (filters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <TagGroup
        aria-label="Applied filters"
        onRemove={(keys) => keys.forEach((key) => onRemove(String(key)))}
      >
        <TagGroup.List className="flex flex-wrap gap-2">
          {filters.map((filter) => (
            <Tag
              key={filter.id}
              id={filter.id}
              textValue={`${filter.label} ${filter.value}`}
            >
              <span className="text-muted">{filter.label}</span> {filter.value}
              <Tag.RemoveButton />
            </Tag>
          ))}
        </TagGroup.List>
      </TagGroup>
      <Button size="sm" variant="ghost" onPress={onClearAll}>
        Clear
      </Button>
    </div>
  );
}
